import { useTranslation } from 'react-i18next'
import { Card, CardBody } from './Card'
import { Button } from './Button'

type QueryStateProps = {
  state: 'pending' | 'error'
  message?: string
  /** Called with no arguments (e.g. a query's `refetch`, wrapped). */
  onRetry?: () => unknown
}

/** The loading / failed state of a server read, in one shape every page
 * shares (MS-819 shared primitives, C-24). The prototype has no async states
 * at all, so this is a recorded deviation with no prototype state
 * (ARCHITECTURE_DECISIONS.md §42), built from the app's existing pattern
 * (Usage's loading and load-error cards: a plain Card, one line of tertiary
 * or danger text).
 *
 * - `pending`: `common.loading`.
 * - `error`: `message` (default `common.loadError`) plus a Retry button when
 *   `onRetry` is given (MS-819 Q7, decided: option A). */
export function QueryStateCard(props: Readonly<QueryStateProps>) {
  return (
    <Card>
      <CardBody>
        <QueryState {...props} />
      </CardBody>
    </Card>
  )
}

/** The same loading / failed line without its own card, for a read that
 * fills one section of a card that is already on screen (the Excess card's
 * Status, ARCHITECTURE_DECISIONS.md §45). */
export function QueryState({ state, message, onRetry }: Readonly<QueryStateProps>) {
  const { t } = useTranslation()
  return state === 'pending' ? (
    <output className="block text-[12.5px] text-[var(--text-tertiary)]">{t('common.loading')}</output>
  ) : (
    <div role="alert" className="flex items-center justify-between gap-4 flex-wrap">
      <div className="text-[12.5px] text-[var(--danger)]">{message ?? t('common.loadError')}</div>
      {onRetry && (
        <Button variant="ghost" onClick={() => onRetry()}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  )
}
